import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  updateDoc,
  doc,
  getDoc,
  Timestamp,
} from "firebase/firestore";
import { db } from "../firebase";

// Shared helpers for the existing "chats/{chatId}" schema: one document per
// buddy pair with `members: [uidA, uidB]` and a `messages` array field
// (see ChatPage.js / Buddies.js — no per-message subcollection exists today,
// so we keep using the same array-append model rather than introducing a
// parallel messaging system).
//
// Message shapes stored in `messages`:
//   { type: "text",  text, senderId, timestamp }
//   { type: "event", eventId, senderId, timestamp }
// Older messages have no `type` field at all — treat that as "text" for
// backward compatibility (see normalizeMessage below).
//
// Only the eventId is stored for event messages, never a copy of the event
// document — the chat UI fetches the live event by id (through the existing
// events/{eventId} security rule) so it always reflects the current
// title/image/status and never leaks a stale/unauthorized snapshot.

// Finds the existing 1:1 chat between two users, or creates a new empty one.
// Mirrors the find-or-create logic already duplicated in Buddies.js/ChatPage.js.
export async function getOrCreateChat(currentUid, otherUid) {
  const chatsRef = collection(db, "chats");
  const q = query(chatsRef, where("members", "array-contains", currentUid));
  const snapshot = await getDocs(q);

  for (const docSnap of snapshot.docs) {
    const data = docSnap.data();
    if (data.members.includes(otherUid)) {
      return { id: docSnap.id, ...data };
    }
  }

  const newChat = await addDoc(chatsRef, {
    members: [currentUid, otherUid],
    messages: [],
  });
  return { id: newChat.id, members: [currentUid, otherUid], messages: [] };
}

// Sends a plain text message. Used by ChatPage.js/Buddies.js, which already
// keep the chat's live `messages` array in local state — the caller updates
// its own state with the returned message rather than this helper re-reading
// the doc, matching the existing optimistic-update pattern.
export async function sendTextMessage(chatId, senderId, text) {
  const message = { type: "text", text, senderId, timestamp: Timestamp.now() };
  const chatRef = doc(db, "chats", chatId);
  const snap = await getDoc(chatRef);
  const currentMessages = snap.exists() ? snap.data().messages || [] : [];
  await updateDoc(chatRef, { messages: [...currentMessages, message] });
  return message;
}

// chat. Used by the Event Details "Send to Buddy" action, which doesn't keep
// a live `messages` array in memory the way ChatPage.js/Buddies.js do, so it
// takes the chat's current messages explicitly and appends to them.
//
// Firestore rules (chats/{chatId} allow update -> isValidNewMessage) enforce
// that the referenced event is approved AND that both the sender and the
// recipient are currently authorized to view it, so this can never be used
// to leak a draft/rejected event or bypass buddies-only visibility.
export async function sendEventMessageToChat(chatId, currentMessages, senderId, eventId) {
  const message = { type: "event", eventId, senderId, timestamp: Timestamp.now() };
  const chatRef = doc(db, "chats", chatId);
  await updateDoc(chatRef, { messages: [...currentMessages, message] });
  return message;
}

// Normalizes a raw stored message so callers never have to special-case the
// legacy (type-less) shape used by every message sent before this feature.
export function normalizeMessage(msg) {
  if (msg && msg.type === "event") return msg;
  return { ...msg, type: "text" };
}
